import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { type ContentItem as ContentItemType } from '@/common/types/lessonPlan';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  ArrowLeftIcon,
  ArrowSquareOutIcon,
  JoystickIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import ContentFilter from './ContentFilter';
import ContentItem from './ContentItem';
import WordMatrixControlPanel from './WordMatrixControlPanel';
import {
  ActivityType,
  addContentItemToHTML,
  contentItemBank,
  generateSectionHTML,
  getActivityExplainer,
  getActivityLearnMoreUrl,
  getActivitySkill,
  getFormattedActivity,
  getSelectedSectionInfo,
} from './utils';

const GAME_ELIGIBLE_ACTIVITIES = new Set([
  ActivityType.SOUND_RECOGNITION_DRILL,
  ActivityType.SPELLING_DRILL,
  ActivityType.WORD_SUMS,
  ActivityType.DECODING_WORD_CHAIN,
  ActivityType.SPELLING_WORD_CHAIN,
  ActivityType.SENTENCE_DICTATION,
]);

interface Props {
  content: string;
  onContentChange: (content: string) => void;
  selectedSectionId: string | null;
  onSelectedSectionIdChange: (id: string | null) => void;
}

const ControlPanel = ({
  content,
  onContentChange,
  selectedSectionId,
  onSelectedSectionIdChange,
}: Props) => {
  const [visibleRange, setVisibleRange] = useState<{
    min: number;
    max: number;
  }>({
    min: 1,
    max: scopeAndSequence.levels.length,
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Check if the selected section still exists
    if (
      selectedSectionId &&
      !getSelectedSectionInfo(content, selectedSectionId)
    ) {
      onSelectedSectionIdChange(null);
    }
  }, [selectedSectionId, content, onSelectedSectionIdChange]);

  function handleAddSection(type: ActivityType) {
    trackMixpanelEvent('Added section to lesson plan', {
      type,
    });
    const newSection = generateSectionHTML(type);
    onContentChange(content ? content + newSection : newSection);
  }

  function handleAddContent(sectionId: string, contentId: string) {
    const selectedSectionInfo = getSelectedSectionInfo(
      content,
      selectedSectionId,
    );
    if (!selectedSectionInfo) return;
    const contentItem = contentItemBank[selectedSectionInfo.type]
      .flat(1)
      .find((item) => item.id === contentId);
    if (contentItem) {
      onContentChange(addContentItemToHTML(content, sectionId, contentItem));
    }
  }

  function getAvailableItems(level: number): ContentItemType[] {
    if (!selectedSectionId) return [];
    const selectedSectionInfo = getSelectedSectionInfo(
      content,
      selectedSectionId,
    );
    if (!selectedSectionInfo) return [];

    return contentItemBank[selectedSectionInfo.type][level];
  }

  const handleRangeChange = (min: number, max: number) => {
    setVisibleRange({ min, max });
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const filterItemsBySearch = (items: ContentItemType[]): ContentItemType[] => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter((item) => {
      const value = Array.isArray(item.value)
        ? item.value.join(' ')
        : item.value;
      return value.toLowerCase().includes(q);
    });
  };
  return (
    <ControlPanelContainer>
      <ControlPanelTitle>Control panel</ControlPanelTitle>
      <ControlSection>
        {selectedSectionId ? (
          <>
            <ControlHeader onClick={() => onSelectedSectionIdChange(null)}>
              <ArrowLeftIcon size={20} />
              <SelectedText>Back to Activity Selector</SelectedText>
            </ControlHeader>
            {getSelectedSectionInfo(content, selectedSectionId)?.type ===
            ActivityType.WORD_MATRIX ? (
              <WordMatrixControlPanel
                sectionId={selectedSectionId}
                content={content}
                onContentChange={onContentChange}
              />
            ) : (
              <>
                <ContentFilter
                  numLevels={scopeAndSequence.levels.length}
                  onRangeChange={handleRangeChange}
                  onSearchChange={handleSearchChange}
                />

                <h3 className="mt-6">Available items:</h3>
                <ActivitiesContainer>
                  {scopeAndSequence.levels
                    .slice(visibleRange.min - 1, visibleRange.max)
                    .map((levelData, adjustedIndex) => {
                      const actualIndex = adjustedIndex + visibleRange.min - 1;
                      const items = getAvailableItems(actualIndex);
                      const filteredItems = filterItemsBySearch(items);

                      // When searching, hide levels with no matching items
                      if (searchQuery.trim() && filteredItems.length === 0) {
                        return null;
                      }

                      return (
                        <div key={actualIndex} className="mb-6 last:mb-0">
                          <div className="mb-2">
                            <p className="font-medium">
                              Level {actualIndex + 1} - {levelData.description}
                            </p>
                          </div>

                          {filteredItems.length > 0 ? (
                            <ContentItemContainer>
                              {filteredItems.map((item) => (
                                <ContentItem
                                  key={item.id}
                                  item={item}
                                  onClick={() =>
                                    handleAddContent(selectedSectionId, item.id)
                                  }
                                />
                              ))}
                            </ContentItemContainer>
                          ) : (
                            <div className="no-items-message">
                              No new items at this level
                            </div>
                          )}
                        </div>
                      );
                    })}
                </ActivitiesContainer>
              </>
            )}
          </>
        ) : (
          <ActivityList>
            <h3>Add New Activity</h3>
            {Object.values(ActivityType).map((type) => (
              <ActivityCard key={type} onClick={() => handleAddSection(type)}>
                <div className="flex items-center justify-between">
                  <h4>{getFormattedActivity(type)}</h4>
                  {GAME_ELIGIBLE_ACTIVITIES.has(type) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <JoystickIcon className="size-4 text-muted-foreground" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        This activity's content can be used for a Game Level
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <p>{getActivitySkill(type)}</p>
                <p>{getActivityExplainer(type)}</p>
                <Button
                  variant="link"
                  className="p-0 m-0 h-fit gap-1 group"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackMixpanelEvent('Lesson Plan Learn More Clicked', {
                      activity: type,
                    });
                    window.open(getActivityLearnMoreUrl(type), '_blank');
                  }}
                >
                  Learn More
                  <ArrowSquareOutIcon className="size-4" />
                </Button>
              </ActivityCard>
            ))}
          </ActivityList>
        )}
      </ControlSection>
    </ControlPanelContainer>
  );
};

export default ControlPanel;

const ControlPanelContainer = styled.div`
  width: 100%;
  padding: 20px;
  border-right: 1px solid hsl(var(--border));
  background-color: hsl(var(--background));
  font-family: var(--font-sans);
  color: hsl(var(--foreground));
  overflow-y: auto;
  height: 100%;
  font-size: 14px;
`;

const ControlPanelTitle = styled.h2`
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
  color: hsl(var(--foreground));
`;

const ControlSection = styled.div`
  padding-top: 10px;
`;

const ControlHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 16px;
  color: hsl(var(--primary));
  font-size: 14px;

  svg {
    margin-right: 8px;
  }

  &:hover {
    color: hsl(var(--primary) / 0.8);
  }
`;

const SelectedText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: hsl(var(--foreground));
`;

const ContentItemContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const ActivitiesContainer = styled.div`
  padding: 8px 0;

  h3 {
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 12px;
    color: hsl(var(--foreground));
  }

  p.font-medium {
    font-size: 13px;
    color: hsl(var(--muted-foreground));
  }

  .no-items-message {
    padding: 12px;
    background-color: hsl(var(--muted));
    border-radius: var(--radius);
    color: hsl(var(--muted-foreground));
    font-size: 13px;
  }
`;

const ActivityList = styled.div`
  margin-bottom: 16px;

  h3 {
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 12px;
    color: hsl(var(--foreground));
  }
`;

const ActivityCard = styled.div`
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  padding: 12px;
  margin-bottom: 8px;
  background-color: hsl(var(--background));
  cursor: pointer;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  }

  h4 {
    font-size: 15px;
    color: hsl(var(--foreground));
    margin-bottom: 4px;
    font-weight: 500;
  }

  p {
    font-size: 13px;
    color: hsl(var(--muted-foreground));
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &:hover {
    background-color: hsl(var(--accent));

    &::before {
      box-shadow:
        0 0 0 1px hsl(var(--border)),
        0 3px 8px -1px rgb(0 0 0 / 0.1),
        0 1px 4px -2px rgb(0 0 0 / 0.1);
    }
  }

  &:active {
    background-color: hsl(var(--accent));
    transform: scale(0.98);
  }
`;
